import { ParkingSessionClient, PagedResultDTOOfParkingSessionDTO, PenaltyState, FilterCondition } from '../../base/models/models.swagger.generated';
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import {
  FilterDTO,
  SortDirection,
  FilterOperator,
} from 'src/app/base/models/models.swagger.generated';
import { ParkerOffenseFilterType } from 'src/app/base/models/parker-offense-filter-type.enum';
import { ClientService } from 'src/app/base/services/client/client.service';
import { ShowInactiveService } from 'src/app/base/shared/grid-base/show-inactive.service';
import { IAdaptorService } from 'src/app/base/shared/syncfusion/adaptor-service.interface';
import { FilterUtil, LICENSE_PLATE_FIELDS } from 'src/app/base/shared/filter-util';
import { DATE_HELPER_DEFAULT_TIME_ZONE } from 'src/app/base/shared/date-helper';

export const controlPlateField = "ControlPlate";
export const countryCodeField = "CountryCode";

@Injectable({
  providedIn: 'root'
})
export class ParkingSessionService  extends ShowInactiveService implements IAdaptorService {

  public offenseType: ParkerOffenseFilterType;
  public zone: number;
  public present: boolean;
  public parkerName: string | null = null;
  public licensePlate: string | null = null;

  constructor(private parkingSessionClient: ParkingSessionClient,
              private clientService: ClientService) {
    super();
  }

  public getData(skip: number, take: number, filters: FilterDTO[], sortBy: string, sortDirection: SortDirection): Observable<PagedResultDTOOfParkingSessionDTO> {
    if (!sortBy) {
      sortBy = 'FirstEntryDateTime';
      sortDirection = SortDirection.Descending;
    }
    if (!this.showInactive) {
      if (!filters) {
        filters = [];
      }
      filters.push(new FilterDTO({isComplex: false, field: 'Active', operator: FilterOperator.Equal, value: 'true'}));
    }

    this.addFilters(filters);
    FilterUtil.addParkerNameFilter(filters, this.parkerName);
    FilterUtil.addLicensePlateFilter(filters, this.licensePlate);
    FilterUtil.correctDateTimeFilters(filters, 'FirstEntryDateTime', DATE_HELPER_DEFAULT_TIME_ZONE);
    this.adjustControlPlateFilters(filters);
    this.adjustCountryCodeFilters(filters);

    return this.parkingSessionClient.searchParkingSessions(this.clientService.selectedClient?.id, filters, skip, take, sortBy, sortDirection).pipe(tap((result: PagedResultDTOOfParkingSessionDTO) => {
      result.items.forEach((item) => {
        (item as any)[controlPlateField] = item.knownTag
          ? item.knownTag.controlPlateNumber
          : item.unknownParkerTagControlPlateNumber;
        (item as any)[countryCodeField] = item.knownTag
          ? item.knownTag.countryCode
          : item.unknownParkerTagCountryCode;
        let hasOnlyCancelledPenalties = true;
        item.parkerRegistrations.forEach((registration) => {
          if (registration.penaltyIssued && registration.penaltyState !== PenaltyState.Cancelled) {
            hasOnlyCancelledPenalties = false;
          }
        });
        if (item.calculationRoadmap != null) {
          item.calculationRoadmap = item.calculationRoadmap.replace(/\r\n/g, '<br>');
        }
        if (hasOnlyCancelledPenalties && item.penaltyIssued) {
          (item as any).penaltyState = PenaltyState.Cancelled;
        }
      });
    }));
  }

  public addRecord(data: any): Observable<any> {
    throw new Error('Method not implemented.');
  }

  public updateRecord(data: any): Observable<any> {
    throw new Error('Method not implemented.');
  }

  public deleteRecord(id: number): Observable<any> {
    throw new Error('Method not implemented.');
  }

  private addFilters(filters: FilterDTO[]): void {
    if (!filters.length) {
      filters = [];
    }

    if (this.offenseType === ParkerOffenseFilterType.Parker) {
      filters.push(new FilterDTO({
        isComplex: false,
        field: 'HasOffense',
        operator: FilterOperator.Equal,
        value: 'true'
      }));
    } else if (this.offenseType === ParkerOffenseFilterType.Offense) {
      filters.push(new FilterDTO({
        isComplex: false,
        field: 'PenaltyIssued',
        operator: FilterOperator.Equal,
        value: 'true'}));
    } else if (this.offenseType === ParkerOffenseFilterType.ParkingRangerEntry) {
      filters.push(new FilterDTO({
        isComplex: false,
        field: 'IsManuallyAdded',
        operator: FilterOperator.Equal,
        value: 'true'
      }));
    }

    if (this.zone) {
      filters.push(new FilterDTO({
        isComplex: false,
        field: 'RegisteredZoneId',
        operator: FilterOperator.Equal,
        value: this.zone.toString()
      }));
    }

    if (this.present != null) {
      filters.push(new FilterDTO({
        isComplex: false,
        field: 'IsClosed',
        operator: FilterOperator.Equal,
        value: (!this.present).toString(),
      }));
    }
  }

  private adjustControlPlateFilters(filters: FilterDTO[]): void {
    if (!filters) {
      filters = [];
    }
    FilterUtil.extendFiltersToMultiFieldFilter(filters, controlPlateField, LICENSE_PLATE_FIELDS, FilterCondition.Or);
  }

  private adjustCountryCodeFilters(filters: FilterDTO[]): void {
    if (!filters) {
      filters = [];
    }
    const countryCodeFilters = FilterUtil.FindAllFiltersWithSpecificField(countryCodeField, filters);
    const filterCondition = countryCodeFilters.length > 0 && countryCodeFilters[0].operator === FilterOperator.Empty
      ? FilterCondition.And
      : FilterCondition.Or;
    FilterUtil.extendFiltersToMultiFieldFilter(filters, countryCodeField, [
      "KnownTag.CountryCode",
      "UnknownParkerTagCountryCode",
    ], filterCondition);
  }
}
